<script setup lang="ts">
  import { useDebounceFn } from '@vueuse/core';
  import { ref, watch } from 'vue';

  import type { Partner } from '@stuut/api/models';
  import { DEBOUNCE_TIME_MS } from '@stuut/common/src/constants';
  import { stuut } from '@stuut/dashboard/src/api';
  import { toast } from '@ui/components/sonner';

  import LanguageCodeSelect from '@/components/common/LanguageCodeSelect.vue';
  import { getApiErrorMessage } from '@/utils/apiErrors';

  const props = defineProps<{
    partner: Partner;
  }>();

  const selectedLanguageCode = ref<string | null>(props.partner?.retell_language_code ?? null);

  const isSaving = ref(false);

  const debouncedUpdate = useDebounceFn(async () => {
    if (isSaving.value) return;

    isSaving.value = true;
    try {
      await stuut.partners.update(props.partner.id, {
        data: {
          retell_language_code: selectedLanguageCode.value,
        },
      });
      toast.success('Language updated.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update partner language.'));
    } finally {
      isSaving.value = false;
    }
  }, DEBOUNCE_TIME_MS);

  watch(
    () => selectedLanguageCode.value,
    () => {
      debouncedUpdate();
    },
  );
</script>

<template>
  <LanguageCodeSelect
    v-model="selectedLanguageCode"
    placeholder="Default"
    clear-label="Use default"
    clearable
    trigger-class="h-7 text-xs text-center justify-between"
  />
</template>
